import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, firstValueFrom, map, Observable, Subscription } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Notificacion } from '../../interior/componentes/header/utilidades/notificacion.class';
import {
  NotificacionDTO,
  TipoNotificacion,
} from '../../interior/componentes/header/utilidades/header.models';
import { ListaDTO } from 'src/app/compartido/utilidades/compartido.models';
import { ErrorFrontend } from 'src/app/compartido/utilidades/error.utils';
import { WebSocketsService } from './websockets.service';
import { environment } from 'src/environments/environment';
import { UsuarioDTO } from '../autenticacion/autenticacion.models';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root',
  deps: [HttpClient, WebSocketsService, MatSnackBarModule],
})
export class NotificacionesService {
  /* Notificaciones */
  private _notificaciones: Notificacion[] = [];
  private _notificaciones$: BehaviorSubject<Notificacion[]> = new BehaviorSubject(
    this._notificaciones,
  );
  public notificaciones$: Observable<Notificacion[]> = this._notificaciones$.asObservable();

  /* Notificaciones no leídas */
  public noLeidas$: Observable<number> = this._notificaciones$.pipe(
    map((notificaciones) => notificaciones.filter((n) => !n.leida).length),
  );

  /* Suscripción */
  private _suscripcion = new Subscription();

  constructor(
    private readonly _http: HttpClient,
    private readonly _webSocketsService: WebSocketsService,
    private readonly _barraAlertas: MatSnackBar,
  ) {}

  /**
   * Inicializa el websocket de notificaciones.
   * @author Juan Corral
   */
  public escucharWebSocket$(_usuario: UsuarioDTO): void {
    this._webSocketsService
      .crearSocket$('notificaciones', environment.wsUrl + '/comunicaciones/notificaciones/', true)
      .then(() => {
        const ecucharWeboscket = this._webSocketsService
          .escucharMensajes$('notificaciones')
          .subscribe((notificacion: NotificacionDTO) => {
            if (!notificacion) return;
            // Añadir la notificación a la lista de notificaciones
            const obj = Notificacion.fromDTO(notificacion);
            this._notificaciones = this._notificaciones.filter((n) => n.id !== obj.id);
            this._notificaciones.unshift(obj);
            this._notificaciones$.next(this._notificaciones);

            // Mostrar la notificación en la barra de alertas. Las informativas van sin
            // clase: una cadena vacía en panelClass revienta en classList.add().
            const clase =
              notificacion.tipo === TipoNotificacion.ERROR
                ? 'alerta-error'
                : notificacion.tipo === TipoNotificacion.EXITO
                  ? 'alerta-exito'
                  : undefined;
            this._barraAlertas.open(obj.mensaje, 'Cerrar', {
              duration: 7000,
              panelClass: clase ? [clase] : undefined,
              horizontalPosition: 'right',
              verticalPosition: 'top',
            });

            // Sonar audio de notificación; es opcional, así que si el
            // navegador lo bloquea o no logra cargarlo se ignora el fallo
            const audio = new Audio('assets/audio/notificacion.mp3');
            audio.play().catch(() => {});
          });
        this._suscripcion.add(ecucharWeboscket);
      });
  }

  /**
   * Obtiene las notificaciones del servidor.
   * @param {number} offset (opcional) - El offset desde el cual empezar a obtener las notificaciones.
   * @returns {Promise<Orden> } - Las notificaciones del servidor (promise).
   * @throws {ErrorFrontend} - Si hubo un error al obtener las notificaciones.
   * @author Juan Corral
   */
  private _obtenerNotificaciones$(offset: number = 0): Promise<Notificacion[]> {
    const params = { offset };
    return firstValueFrom(
      this._http.get<ListaDTO<NotificacionDTO>>('comunicaciones/notificaciones/', { params }).pipe(
        map((res: ListaDTO<NotificacionDTO>) =>
          res.results.map((notificacion) => Notificacion.fromDTO(notificacion)),
        ),
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Actualiza las notificaciones.
   * @author Juan Corral
   */
  public async actualizarNotificaciones$(): Promise<void> {
    this._notificaciones = await this._obtenerNotificaciones$();
    this._notificaciones$.next(this._notificaciones);
  }

  /**
   * Carga más notificaciones.
   * @author Juan Corral
   */
  public async cargarMasNotificaciones$(): Promise<void> {
    const nuevas = await this._obtenerNotificaciones$(this._notificaciones.length);
    this._notificaciones = this._notificaciones.concat(nuevas);
    this._notificaciones$.next(this._notificaciones);
  }

  /**
   * Marca una notificación como leída.
   * @param {Notificacion} notificacion - La notificación a marcar como leída.
   * @returns {Promise<void>} - Promise que se resuelve cuando la notificación ha sido marcada como leída.
   * @author Juan Corral
   */
  public async marcarLeida$(notificacion: Notificacion): Promise<void> {
    await firstValueFrom(
      this._http.post<NotificacionDTO>(
        'comunicaciones/notificaciones/' + notificacion.id + '/marcar_leida/',
        {},
      ),
    );

    this._notificaciones = this._notificaciones.map((n) => {
      if (n.id === notificacion.id) n.leida = true;
      return n;
    });
    this._notificaciones$.next(this._notificaciones);
  }

  /**
   * Limpia los datos almacenados en el servicio.
   * @author Juan Corral
   */
  public limpiar(): void {
    this._webSocketsService.cerrarSocket('notificaciones');
    this._notificaciones = [];
    this._notificaciones$.next(this._notificaciones);
    this._suscripcion.unsubscribe();
    this._suscripcion = new Subscription();
  }
}
